import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import ErrorDisplay from './ErrorDisplay';
import DocumentViewerModal from './DocumentViewerModal';
import { roundUpToQuarter } from '../utils/priceRounding';

export default function CargoInsuranceModal({ isOpen, onClose, onSubmit, initialValues, branch }) {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Error and submission state
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [draftPolicy, setDraftPolicy] = useState(null);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);

  // Transport method options
  const transportMethods = [
    { id: 'sea', name: t('cargo.transportMethods.sea') },
    { id: 'road', name: t('cargo.transportMethods.road') },
    { id: 'air', name: t('cargo.transportMethods.air') }
  ];

  const [formData, setFormData] = useState({
    policyType: 'Cargo Transport Insurance',
    cargoInsuranceSubType: 'sea', // Default to sea
    // Insured party information
    insuredName: '',
    insuredAddress: '',
    // Cargo details
    cargoDescription: '',
    invoiceNumber: '',
    bankName: '', // Optional
    creditNumber: '', // Optional
    // Values for calculation
    documentaryValue: '',
    documentaryCurrency: 'USD', // Default to USD
    exchangeRateWithTax: '',
    insuranceAmount: '', // Calculated: documentaryValue * exchangeRateWithTax
    insuranceAmountInWords: '',
    // Journey details
    journeyFrom: '',
    journeyTo: '',
    transportMethod: '',
    groupConditions: 'A', // Default to A
    // Premium calculation inputs
    cargoRate: '', // User input (percentage)
    vesselAgePercentage: 0, // Default 0
    warPercentage: 0, // Default 0
    // Calculated premium fields
    basicPremium: 0, // cargoRate * insuranceAmount
    vesselAgeCost: 0, // vesselAgePercentage * insuranceAmount
    warCost: 0, // warPercentage * insuranceAmount
    netPremiumCargo: 0, // basicPremium + vesselAgeCost + warCost
    // Standard fields
    tax: 0, // 1% of netPremiumCargo
    supervisionFees: 0, // 50% of tax
    stampDuty: 0.50, // Editable, default 0.50
    issueFees: 10, // Editable, default 10
    totalPremium: 0, // Sum of all
    premium: 0, // Same as netPremiumCargo for compatibility
    limitOfIndemnity: 0, // Not used for cargo insurance
    // Payment and dates
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'), // Same day for one-time
    numberOfDays: 1, // One-time payment
    paymentStatus: 'unpaid',
    paymentMethod: '',
    notes: ''
  });

  useEffect(() => {
    if (initialValues && isOpen) {
      setFormData(prev => ({
        ...prev,
        ...initialValues,
        policyType: 'Cargo Transport Insurance'
      }));
    } else if (isOpen && !initialValues) {
      // Reset form for new policy
      setFormData({
        policyType: 'Cargo Transport Insurance',
        cargoInsuranceSubType: 'sea',
        insuredName: '',
        insuredAddress: '',
        cargoDescription: '',
        invoiceNumber: '',
        bankName: '',
        creditNumber: '',
        documentaryValue: '',
        exchangeRateWithTax: '',
        insuranceAmount: '',
        insuranceAmountInWords: '',
        journeyFrom: '',
        journeyTo: '',
        transportMethod: '',
        groupConditions: 'A',
        cargoRate: '',
        vesselAgePercentage: 0,
        warPercentage: 0,
        basicPremium: 0,
        vesselAgeCost: 0,
        warCost: 0,
        netPremiumCargo: 0,
        tax: 0,
        supervisionFees: 0,
        stampDuty: 0.50,
        issueFees: 10,
        totalPremium: 0,
        premium: 0,
        limitOfIndemnity: 0,
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        numberOfDays: 1,
        paymentStatus: 'unpaid',
        paymentMethod: '',
        notes: ''
      });
    }
  }, [initialValues, isOpen]);

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  // Function to convert number to Arabic words
  const numberToArabicWords = (num) => {
    if (!num || isNaN(num)) return '';
    
    const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
    const tens = ['', 'عشرة', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
    const hundreds = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];
    const thousands = ['', 'ألف', 'ألفان', 'آلاف'];
    
    // For simplicity, convert to string representation
    // A full implementation would need complex Arabic numeral rules
    const numStr = parseFloat(num).toFixed(2);
    return `${numStr} دينار ليبي فقط`;
  };

  // Calculate insurance amount when documentary value or exchange rate changes
  useEffect(() => {
    if (formData.documentaryValue && formData.exchangeRateWithTax) {
      const docValue = parseFloat(formData.documentaryValue) || 0;
      const exchangeRate = parseFloat(formData.exchangeRateWithTax) || 0;
      const insuranceAmount = docValue * exchangeRate;
      
      setFormData(prev => ({
        ...prev,
        insuranceAmount: insuranceAmount.toFixed(2),
        insuranceAmountInWords: numberToArabicWords(insuranceAmount)
      }));
    }
  }, [formData.documentaryValue, formData.exchangeRateWithTax]);

  // Calculate all premiums when relevant fields change
  useEffect(() => {
    const insuranceAmount = parseFloat(formData.insuranceAmount) || 0;
    const cargoRate = parseFloat(formData.cargoRate) || 0;
    
    // Only include vessel age and war for SEA transport
    const vesselAgePercentage = formData.cargoInsuranceSubType === 'sea' ? (parseFloat(formData.vesselAgePercentage) || 0) : 0;
    const warPercentage = formData.cargoInsuranceSubType === 'sea' ? (parseFloat(formData.warPercentage) || 0) : 0;
    
    // Calculate basic premium: cargoRate (as percentage) * insuranceAmount
    let basicPremium = (cargoRate / 100) * insuranceAmount;
    // Round basic premium up to nearest 0.25 increment
    basicPremium = roundUpToQuarter(basicPremium);
    
    // Calculate vessel age cost (only for sea)
    let vesselAgeCost = (vesselAgePercentage / 100) * insuranceAmount;
    // Round vessel age cost up to nearest 0.25 increment
    vesselAgeCost = roundUpToQuarter(vesselAgeCost);
    
    // Calculate war cost (only for sea)
    let warCost = (warPercentage / 100) * insuranceAmount;
    // Round war cost up to nearest 0.25 increment
    warCost = roundUpToQuarter(warCost);
    
    // Calculate net premium
    let netPremiumCargo = basicPremium + vesselAgeCost + warCost;
    // Round net premium up to nearest 0.25 increment
    netPremiumCargo = roundUpToQuarter(netPremiumCargo);
    
    // Calculate tax (1% of net premium)
    let tax = netPremiumCargo * 0.01;
    // Round tax up to nearest 0.25 increment
    tax = roundUpToQuarter(tax);
    
    // Calculate supervision fees (50% of tax)
    let supervisionFees = tax * 0.5;
    // Round supervision fees up to nearest 0.25 increment
    supervisionFees = roundUpToQuarter(supervisionFees);
    
    // Get editable fees
    const stampDuty = parseFloat(formData.stampDuty) || 0;
    const issueFees = parseFloat(formData.issueFees) || 0;
    
    // Calculate total premium
    let totalPremium = netPremiumCargo + tax + supervisionFees + stampDuty + issueFees;
    // Round total premium up to nearest 0.25 increment
    totalPremium = roundUpToQuarter(totalPremium);
    
    setFormData(prev => ({
      ...prev,
      basicPremium: basicPremium.toFixed(2),
      vesselAgeCost: vesselAgeCost.toFixed(2),
      warCost: warCost.toFixed(2),
      netPremiumCargo: netPremiumCargo.toFixed(2),
      premium: netPremiumCargo.toFixed(2), // For compatibility
      tax: tax.toFixed(2),
      supervisionFees: supervisionFees.toFixed(2),
      totalPremium: totalPremium.toFixed(2)
    }));
  }, [
    formData.insuranceAmount,
    formData.cargoRate,
    formData.vesselAgePercentage,
    formData.warPercentage,
    formData.stampDuty,
    formData.issueFees,
    formData.cargoInsuranceSubType // Re-calculate when sub-type changes
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for sub-type change
    if (name === 'cargoInsuranceSubType') {
      if (value === 'road') {
        // For land/road: set transport method to trucks, reset vessel age and war
        setFormData(prev => ({
          ...prev,
          [name]: value,
          transportMethod: 'الشــاحنـــات',
          vesselAgePercentage: 0,
          warPercentage: 0
        }));
      } else if (value === 'air') {
        // For air: reset vessel age and war, clear transport method
        setFormData(prev => ({
          ...prev,
          [name]: value,
          transportMethod: '',
          vesselAgePercentage: 0,
          warPercentage: 0
        }));
      } else {
        // For sea: clear transport method, allow vessel age and war
        setFormData(prev => ({
          ...prev,
          [name]: value,
          transportMethod: ''
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const prepareSubmissionData = () => {
    // Get branch and company IDs with fallback to user's data
    const branchId = branch?.id || user?.branchId;
    const companyId = branch?.companyId || user?.companyId;
    
    console.log('[Cargo Modal] Preparing submission data:', {
      branch,
      user,
      branchId,
      companyId
    });
    
    return {
      id: draftPolicy?.id || initialValues?.id,
      policyType: 'Cargo Transport Insurance',
      // Branch and company information (REQUIRED)
      branchId,
      companyId,
      // Cargo-specific fields
      cargoInsuranceSubType: formData.cargoInsuranceSubType,
      insuredName: formData.insuredName,
      insuredAddress: formData.insuredAddress,
      cargoDescription: formData.cargoDescription,
      invoiceNumber: formData.invoiceNumber,
      bankName: formData.bankName,
      creditNumber: formData.creditNumber,
      documentaryValue: formData.documentaryValue,
      documentaryCurrency: formData.documentaryCurrency,
      exchangeRateWithTax: formData.exchangeRateWithTax,
      insuranceAmountInWords: formData.insuranceAmountInWords,
      journeyFrom: formData.journeyFrom,
      journeyTo: formData.journeyTo,
      transportMethod: formData.transportMethod,
      groupConditions: formData.groupConditions,
      cargoRate: formData.cargoRate,
      vesselAgePercentage: formData.vesselAgePercentage,
      warPercentage: formData.warPercentage,
      basicPremium: formData.basicPremium,
      vesselAgeCost: formData.vesselAgeCost,
      warCost: formData.warCost,
      netPremiumCargo: formData.netPremiumCargo,
      // Standard fields
      limitOfIndemnity: 0,
      premium: formData.netPremiumCargo,
      tax: formData.tax,
      supervisionFees: formData.supervisionFees,
      stampDuty: formData.stampDuty,
      issueFees: formData.issueFees,
      totalPremium: formData.totalPremium,
      numberOfDays: 1,
      startDate: moment(formData.startDate).toISOString(),
      endDate: moment(formData.endDate).toISOString(),
      paymentStatus: formData.paymentStatus,
      paymentMethod: formData.paymentStatus === 'paid' ? formData.paymentMethod : null,
      notes: formData.notes,
      // Not used for cargo insurance but required by schema
      firstName: formData.insuredName.split(' ')[0] || 'N/A',
      fatherName: formData.insuredName.split(' ')[1] || '',
      grandfatherName: formData.insuredName.split(' ')[2] || '',
      lastName: formData.insuredName.split(' ').slice(3).join(' ') || '',
      dateOfBirth: '2000-01-01', // Placeholder
      address: formData.insuredAddress,
      nationalId: '',
      mobile: '',
      email: '',
      nationality: '',
      passportNumber: '',
      licenseNumber: ''
    };
  };

  const handleSaveDraft = async () => {
    setError(null);
    setIsSavingDraft(true);
    
    try {
      const token = localStorage.getItem('token');
      const submissionData = prepareSubmissionData();
      
      const response = await api.post(
        '/api/policies/draft',
        submissionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDraftPolicy(response.data.policy);
      toast.success(t('policy.draftSaved'));
    } catch (draftError) {
      console.error('Draft save error:', draftError);
      setError(draftError);
      toast.error(t('policy.draftSaveFailed'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePreview = async () => {
    // Must save as draft first before preview
    if (!draftPolicy) {
      await handleSaveDraft();
    }
    setIsPreviewOpen(true);
  };

  const handleActivate = async () => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      if (!draftPolicy) {
        throw new Error('Please save as draft first');
      }
      
      if (formData.paymentStatus !== 'paid') {
        throw new Error(t('policy.paymentRequired'));
      }
      
      const token = localStorage.getItem('token');
      const response = await api.post(
        `/api/policies/${draftPolicy.id}/activate`,
        {
          startDate: moment(formData.startDate).toISOString(),
          paymentMethod: formData.paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const activatedPolicy = response.data.policy;
      toast.success(t('policy.policyActivated'));
      
      // Automatically generate and download the document
      setIsGeneratingDocument(true);
      try {
        const docResponse = await api.post(
          `/api/documents/generate-from-policy/${activatedPolicy.id}`,
          { format: 'a4' }, // Default to A4 format
          { responseType: 'blob' }
        );

        const blob = docResponse.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Cargo_Policy_${activatedPolicy.policyNumber}_A4.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success(t('policies.documentGenerated'));
      } catch (docError) {
        console.error('Document generation failed:', docError);
        toast.error(t('policies.generateError'));
      } finally {
        setIsGeneratingDocument(false);
      }
      
      handleClose();
      if (onSubmit) {
        onSubmit(activatedPolicy);
      }
    } catch (activateError) {
      console.error('Activation error:', activateError);
      setError(activateError);
      toast.error(activateError.response?.data?.message || t('policy.activationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent default form submission - use the 3 buttons instead
  };

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-6xl sm:w-full sm:p-6">
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                        {initialValues ? t('cargo.editTitle') : t('cargo.title')}
                      </Dialog.Title>

                      {error && (
                        <ErrorDisplay 
                          error={error} 
                          onDismiss={() => setError(null)} 
                          className="mt-4 mb-4"
                        />
                      )}

                      <form onSubmit={handleSubmit} className="mt-6">
                        <div className="grid grid-cols-6 gap-6">
                          
                          {/* Policy Type - Fixed */}
                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.policyType')}
                            </label>
                            <input
                              type="text"
                              value={t('cargo.cargoTransportInsurance')}
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                              readOnly
                            />
                          </div>

                          {/* Sub-type Selector */}
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="cargoInsuranceSubType" className="block text-sm font-medium text-gray-700">
                              {t('cargo.subType')}
                            </label>
                            <select
                              name="cargoInsuranceSubType"
                              id="cargoInsuranceSubType"
                              value={formData.cargoInsuranceSubType}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              {transportMethods.map((method) => (
                                <option key={method.id} value={method.id}>
                                  {method.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Insured Party Information */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.insuredPartyInfo')}
                            </h4>
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="insuredName" className="block text-sm font-medium text-gray-700">
                              {t('cargo.insuredName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="insuredName"
                              id="insuredName"
                              value={formData.insuredName}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="insuredAddress" className="block text-sm font-medium text-gray-700">
                              {t('cargo.insuredAddress')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="insuredAddress"
                              id="insuredAddress"
                              value={formData.insuredAddress}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          {/* Cargo Details */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.cargoDetails')}
                            </h4>
                          </div>

                          <div className="col-span-6">
                            <label htmlFor="cargoDescription" className="block text-sm font-medium text-gray-700">
                              {t('cargo.cargoDescription')} <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              name="cargoDescription"
                              id="cargoDescription"
                              value={formData.cargoDescription}
                              onChange={handleChange}
                              rows={3}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700">
                              {t('cargo.invoiceNumber')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="invoiceNumber"
                              id="invoiceNumber"
                              value={formData.invoiceNumber}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
                              {t('cargo.bankName')}
                            </label>
                            <input
                              type="text"
                              name="bankName"
                              id="bankName"
                              value={formData.bankName}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="creditNumber" className="block text-sm font-medium text-gray-700">
                              {t('cargo.creditNumber')}
                            </label>
                            <input
                              type="text"
                              name="creditNumber"
                              id="creditNumber"
                              value={formData.creditNumber}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                          </div>

                          {/* Value Calculation */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.valueCalculation')}
                            </h4>
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="documentaryValue" className="block text-sm font-medium text-gray-700">
                              {t('cargo.documentaryValue')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              name="documentaryValue"
                              id="documentaryValue"
                              value={formData.documentaryValue}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-1">
                            <label htmlFor="documentaryCurrency" className="block text-sm font-medium text-gray-700">
                              {t('cargo.currency')} <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="documentaryCurrency"
                              id="documentaryCurrency"
                              value={formData.documentaryCurrency}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="GBP">GBP</option>
                              <option value="LYD">LYD</option>
                            </select>
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="exchangeRateWithTax" className="block text-sm font-medium text-gray-700">
                              {t('cargo.exchangeRateWithTax')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.0001"
                              name="exchangeRateWithTax"
                              id="exchangeRateWithTax"
                              value={formData.exchangeRateWithTax}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.insuranceAmount')}
                            </label>
                            <input
                              type="number"
                              value={formData.insuranceAmount}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.insuranceAmountInWords')}
                            </label>
                            <input
                              type="text"
                              value={formData.insuranceAmountInWords}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            />
                          </div>

                          {/* Journey Details */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.journeyDetails')}
                            </h4>
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="journeyFrom" className="block text-sm font-medium text-gray-700">
                              {t('cargo.journeyFrom')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="journeyFrom"
                              id="journeyFrom"
                              value={formData.journeyFrom}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="journeyTo" className="block text-sm font-medium text-gray-700">
                              {t('cargo.journeyTo')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="journeyTo"
                              id="journeyTo"
                              value={formData.journeyTo}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="transportMethod" className="block text-sm font-medium text-gray-700">
                              {t('cargo.transportMethod')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="transportMethod"
                              id="transportMethod"
                              value={formData.transportMethod}
                              onChange={handleChange}
                              readOnly={formData.cargoInsuranceSubType === 'road'}
                              className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                formData.cargoInsuranceSubType === 'road' ? 'bg-gray-50' : ''
                              }`}
                              required
                            />
                            {formData.cargoInsuranceSubType === 'road' && (
                              <p className="mt-1 text-xs text-gray-500">{t('cargo.trucksOnly')}</p>
                            )}
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="groupConditions" className="block text-sm font-medium text-gray-700">
                              {t('cargo.groupConditions')} <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="groupConditions"
                              id="groupConditions"
                              value={formData.groupConditions}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="A">{t('cargo.groupA')}</option>
                              <option value="B">{t('cargo.groupB')}</option>
                              <option value="C">{t('cargo.groupC')}</option>
                              <option value="LAND_TRANSPORT">{t('cargo.groupLandTransport')}</option>
                            </select>
                          </div>

                          {/* Premium Calculation */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.premiumCalculation')}
                            </h4>
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="cargoRate" className="block text-sm font-medium text-gray-700">
                              {t('cargo.rate')} (%) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.0001"
                              name="cargoRate"
                              id="cargoRate"
                              value={formData.cargoRate}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          {/* Only show vessel age and war for SEA transport */}
                          {formData.cargoInsuranceSubType === 'sea' && (
                            <>
                              <div className="col-span-6 sm:col-span-2">
                                <label htmlFor="vesselAgePercentage" className="block text-sm font-medium text-gray-700">
                                  {t('cargo.vesselAge')} (%)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  name="vesselAgePercentage"
                                  id="vesselAgePercentage"
                                  value={formData.vesselAgePercentage}
                                  onChange={handleChange}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">{t('cargo.defaultZero')}</p>
                              </div>

                              <div className="col-span-6 sm:col-span-2">
                                <label htmlFor="warPercentage" className="block text-sm font-medium text-gray-700">
                                  {t('cargo.warRisk')} (%)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  name="warPercentage"
                                  id="warPercentage"
                                  value={formData.warPercentage}
                                  onChange={handleChange}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">{t('cargo.defaultZero')}</p>
                              </div>
                            </>
                          )}

                          <div className="col-span-6 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.basicPremium')}
                            </label>
                            <input
                              type="number"
                              value={formData.basicPremium}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            />
                          </div>

                          {/* Only show vessel age and war costs for SEA transport */}
                          {formData.cargoInsuranceSubType === 'sea' && (
                            <>
                              <div className="col-span-6 sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t('cargo.vesselAgeCost')}
                                </label>
                                <input
                                  type="number"
                                  value={formData.vesselAgeCost}
                                  readOnly
                                  className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                                />
                              </div>

                              <div className="col-span-6 sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t('cargo.warCost')}
                                </label>
                                <input
                                  type="number"
                                  value={formData.warCost}
                                  readOnly
                                  className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                                />
                              </div>
                            </>
                          )}

                          <div className="col-span-6 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.netPremium')}
                            </label>
                            <input
                              type="number"
                              value={formData.netPremiumCargo}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm font-semibold"
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.tax')} (1%)
                            </label>
                            <input
                              type="number"
                              value={formData.tax}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.supervisionFees')} (50%)
                            </label>
                            <input
                              type="number"
                              value={formData.supervisionFees}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="stampDuty" className="block text-sm font-medium text-gray-700">
                              {t('cargo.stampDuty')}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              name="stampDuty"
                              id="stampDuty"
                              value={formData.stampDuty}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">{t('cargo.editable')}</p>
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="issueFees" className="block text-sm font-medium text-gray-700">
                              {t('cargo.issueFees')}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              name="issueFees"
                              id="issueFees"
                              value={formData.issueFees}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">{t('cargo.editable')}</p>
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('cargo.totalPremium')}
                            </label>
                            <input
                              type="number"
                              value={formData.totalPremium}
                              readOnly
                              className="mt-1 block w-full border border-gray-300 bg-green-50 rounded-md shadow-sm py-2 px-3 sm:text-sm font-bold text-green-700"
                            />
                          </div>

                          {/* Payment Information */}
                          <div className="col-span-6">
                            <h4 className="text-md font-medium text-gray-900 border-t pt-4">
                              {t('cargo.paymentInfo')}
                            </h4>
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                              {t('cargo.date')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              name="startDate"
                              id="startDate"
                              value={formData.startDate}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  startDate: e.target.value,
                                  endDate: e.target.value // Same day
                                }));
                              }}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-2">
                            <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">
                              {t('cargo.paymentStatus')}
                            </label>
                            <select
                              name="paymentStatus"
                              id="paymentStatus"
                              value={formData.paymentStatus}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="unpaid">{t('cargo.unpaid')}</option>
                              <option value="paid">{t('cargo.paid')}</option>
                            </select>
                          </div>

                          {formData.paymentStatus === 'paid' && (
                            <div className="col-span-6 sm:col-span-2">
                              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                                {t('cargo.paymentMethod')}
                              </label>
                              <select
                                name="paymentMethod"
                                id="paymentMethod"
                                value={formData.paymentMethod}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                required
                              >
                                <option value="">{t('cargo.selectPaymentMethod')}</option>
                                <option value="cash">{t('cargo.cash')}</option>
                                <option value="cheque">{t('cargo.cheque')}</option>
                                <option value="on account">{t('cargo.onAccount')}</option>
                              </select>
                            </div>
                          )}

                          {/* Terms and Conditions for Land/Air Transport */}
                          {(formData.cargoInsuranceSubType === 'road' || formData.cargoInsuranceSubType === 'air') && (
                            <div className="col-span-6">
                              <div className="bg-yellow-50 border-r-4 border-yellow-400 p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  {t('cargo.termsAndConditions')}
                                </h4>
                                <p className="text-xs text-gray-700 leading-relaxed" style={{ direction: 'rtl', textAlign: 'right' }}>
                                  من المعلوم أن جميع الشروط والبنود والاستثناءات المرفقة وكذلك الملاحق والشهادات الصادره علي قوة هذة الوثيقة تعتبر جزءا مكملا لا يتجزا منها.
                                </p>
                                <p className="text-xs text-gray-700 leading-relaxed mt-2" style={{ direction: 'rtl', textAlign: 'right' }}>
                                  شروط التأمين الصادرة عن مجمع مكتتبي التأمين بلندن في 1982/01/01
                                </p>
                                <p className="text-xs font-semibold text-red-700 leading-relaxed mt-2" style={{ direction: 'rtl', textAlign: 'right' }}>
                                  تغطي هذه الوثيقة الأضرار الناتجة عن انقلاب أو احتراق أو إصطدام وسيلة الناقلة أثناء الرحلــة البــريــــــة
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="col-span-6">
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                              {t('cargo.notes')}
                            </label>
                            <textarea
                              name="notes"
                              id="notes"
                              value={formData.notes}
                              onChange={handleChange}
                              rows={3}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                          </div>

                        </div>

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-4 sm:gap-3">
                          {/* Save as Draft Button */}
                          <button
                            type="button"
                            onClick={handleSaveDraft}
                            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isSavingDraft || isSubmitting}
                          >
                            {isSavingDraft ? t('policy.saving') : t('policy.saveDraft')}
                          </button>

                          {/* Preview Button */}
                          <button
                            type="button"
                            onClick={handlePreview}
                            className="w-full inline-flex justify-center rounded-md border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-base font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isSavingDraft || isSubmitting}
                          >
                            {t('policy.preview')}
                          </button>

                          {/* Activate/Create Button (اصدار) */}
                          <button
                            type="button"
                            onClick={handleActivate}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                            disabled={!draftPolicy || formData.paymentStatus !== 'paid' || isSubmitting || isGeneratingDocument}
                          >
                            {isSubmitting || isGeneratingDocument ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {isGeneratingDocument ? t('cargo.generatingDocument') : t('policy.activating')}
                              </>
                            ) : (
                              t('policy.activate')
                            )}
                          </button>

                          {/* Cancel Button */}
                          <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onClick={handleClose}
                            disabled={isSubmitting || isSavingDraft}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Document Viewer Modal for Preview */}
      {isPreviewOpen && draftPolicy && (
        <DocumentViewerModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          policy={draftPolicy}
          format="a4"
        />
      )}
    </>
  );
}

